/**
 * Club tournament scheduler (DB-free).
 * Snake-seed pools, round-robin, auto finals, pack matches onto tables.
 */

const POOL_LETTERS = "ABCDEFGHJKLMNPQRSTUVWXYZ";

function clampInt(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.round(n)));
}

function sortPlayers(players) {
  return [...(players || [])].sort((a, b) => {
    const ra = a.ranking_points == null ? null : Number(a.ranking_points);
    const rb = b.ranking_points == null ? null : Number(b.ranking_points);
    if (ra == null && rb == null) {
      return String(a.name || "").localeCompare(String(b.name || ""));
    }
    if (ra == null) return 1;
    if (rb == null) return -1;
    if (rb !== ra) return rb - ra;
    return String(a.name || "").localeCompare(String(b.name || ""));
  });
}

function poolSizesFor(n, poolSize) {
  const size = clampInt(poolSize, 3, 8, 4);
  const count = Math.max(0, Number(n) || 0);
  if (count < 2) return [];
  if (count === 2) return [2];
  if (count <= size) return [count];

  let pools = Math.ceil(count / size);
  const build = (p) => {
    const base = Math.floor(count / p);
    const extra = count % p;
    return Array.from({ length: p }, (_, i) => base + (i < extra ? 1 : 0));
  };
  let sizes = build(pools);
  while (Math.min(...sizes) < 3 && pools > 1) {
    pools -= 1;
    sizes = build(pools);
  }
  return sizes;
}

function snakeSeed(players, sizes) {
  const pools = sizes.map((size, i) => ({
    letter: POOL_LETTERS[i] || String(i + 1),
    size,
    players: [],
  }));
  if (!pools.length) return pools;
  let dir = 1;
  let index = 0;
  for (const player of players) {
    pools[index].players.push(player);
    if (pools.length === 1) continue;
    index += dir;
    if (index >= pools.length) {
      dir = -1;
      index = pools.length - 1;
    } else if (index < 0) {
      dir = 1;
      index = 0;
    }
  }
  return pools;
}

function roundRobinRounds(playerIds) {
  const ids = (playerIds || []).filter(Boolean);
  if (ids.length < 2) return [];
  const list = ids.slice();
  if (list.length % 2 === 1) list.push(null);
  const n = list.length;
  const rounds = n - 1;
  const half = n / 2;
  const out = [];
  const arr = list.slice();
  for (let r = 0; r < rounds; r += 1) {
    const pairs = [];
    for (let i = 0; i < half; i += 1) {
      const a = arr[i];
      const b = arr[n - 1 - i];
      if (a && b) pairs.push([a, b]);
    }
    out.push(pairs);
    const last = arr.pop();
    arr.splice(1, 0, last);
  }
  return out;
}

function pickFinalsMode(qualifierCount, override) {
  const mode = String(override || "auto").toLowerCase();
  if (mode === "knockout" || mode === "final_table") return mode;
  if (qualifierCount <= 6) return "final_table";
  return "knockout";
}

function nextPowerOfTwo(n) {
  let p = 1;
  while (p < n) p *= 2;
  return p;
}

function knockoutRoundLabel(remaining) {
  if (remaining <= 2) return "Final";
  if (remaining <= 4) return "Semi-final";
  if (remaining <= 8) return "Quarter-final";
  return `Round of ${remaining}`;
}

/**
 * Pair 1st of pool i with 2nd of the next pool when possible.
 */
function knockoutSeeds(pools, qualifyCount) {
  const firsts = [];
  const rest = [];
  pools.forEach((pool) => {
    const q = Math.min(qualifyCount, pool.players.length);
    for (let place = 1; place <= q; place += 1) {
      const player = pool.players[place - 1];
      const seed = {
        player_id: player?.id || null,
        placeholder: `${place === 1 ? "1st" : `${place}th`} Pool ${pool.letter}`,
        pool_letter: pool.letter,
        place,
      };
      if (place === 1) firsts.push(seed);
      else rest.push(seed);
    }
  });
  return [...firsts, ...rest];
}

function buildKnockoutMatches(categoryId, seeds) {
  const n = seeds.length;
  if (n < 2) return [];
  const size = nextPowerOfTwo(n);
  const slots = Array.from({ length: size }, (_, i) => seeds[i] || null);
  const matches = [];
  let roundSize = size;
  let roundIndex = 0;
  let previous = slots.map((seed, i) => ({
    key: `s${i}`,
    seed,
  }));

  while (roundSize >= 2) {
    const roundMatches = [];
    const label = knockoutRoundLabel(roundSize);
    for (let i = 0; i < roundSize; i += 2) {
      const left = previous[i];
      const right = previous[i + 1];
      const a = left?.seed || null;
      const b = right?.seed || null;
      const isBye = !a || !b;
      const match = {
        category_id: categoryId,
        pool_letter: null,
        stage: "knockout",
        round_key: `ko-${roundSize}`,
        round_label: label,
        player_a_id: a?.player_id || null,
        player_b_id: b?.player_id || null,
        placeholder_a: a?.placeholder || (roundIndex ? "Winner" : null),
        placeholder_b: b?.placeholder || (roundIndex ? "Winner" : null),
        is_bye: isBye,
      };
      if (!isBye) roundMatches.push(match);
    }
    matches.push(...roundMatches);
    previous = Array.from({ length: roundSize / 2 }, (_, i) => ({
      key: `w${roundSize}-${i}`,
      seed: {
        player_id: null,
        placeholder: `Winner ${label} ${i + 1}`,
      },
    }));
    roundSize /= 2;
    roundIndex += 1;
  }
  return matches;
}

function buildFinalTableMatches(categoryId, seeds) {
  const ids = seeds.map((s, i) => s.player_id || `ph:${categoryId}:${i}`);
  const placeholders = Object.fromEntries(
    seeds.map((s, i) => [ids[i], s.placeholder])
  );
  const rounds = roundRobinRounds(ids);
  const matches = [];
  rounds.forEach((pairs, r) => {
    pairs.forEach(([a, b]) => {
      matches.push({
        category_id: categoryId,
        pool_letter: null,
        stage: "final_table",
        round_key: `ft-${r + 1}`,
        round_label: `Final table R${r + 1}`,
        player_a_id: String(a).startsWith("ph:") ? null : a,
        player_b_id: String(b).startsWith("ph:") ? null : b,
        placeholder_a: placeholders[a] || null,
        placeholder_b: placeholders[b] || null,
        is_bye: false,
      });
    });
  });
  return matches;
}

function normalizeTables(tables, tableCount) {
  const fromRows = (tables || [])
    .map((row, i) => ({
      table_id: row.table_id || `virtual-${i + 1}`,
      table_number: Number(row.table_number || i + 1),
    }))
    .sort((a, b) => a.table_number - b.table_number);
  const count = clampInt(tableCount || fromRows.length || 4, 1, 40, 4);
  if (fromRows.length >= count) return fromRows.slice(0, count);
  const out = fromRows.slice();
  for (let n = out.length + 1; n <= count; n += 1) {
    out.push({ table_id: `virtual-${n}`, table_number: n });
  }
  return out;
}

function personKeysForMatch(match) {
  return [match.player_a_id, match.player_b_id].filter(Boolean).map(String);
}

function packMatches(matches, tables) {
  const slots = [];
  return matches.map((match) => {
    if (match.is_bye) {
      return { ...match, slot_index: null, table_id: null, table_number: null };
    }
    const people = personKeysForMatch(match);
    let slotIndex = 0;
    for (;;) {
      if (!slots[slotIndex]) {
        slots[slotIndex] = { used: 0, people: new Set() };
      }
      const slot = slots[slotIndex];
      const tableFree = slot.used < tables.length;
      const peopleFree = people.every((id) => !slot.people.has(id));
      if (tableFree && peopleFree) {
        const table = tables[slot.used];
        slot.used += 1;
        people.forEach((id) => slot.people.add(id));
        return {
          ...match,
          slot_index: slotIndex,
          table_id: table.table_id,
          table_number: table.table_number,
        };
      }
      slotIndex += 1;
    }
  });
}

function summarizeHeadline({
  playerCount,
  poolCount,
  poolMatches,
  finalsMode,
  qualifierCount,
  slotCount,
  matchMinutes,
}) {
  const mins = slotCount * (matchMinutes || 20);
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  const duration =
    hours > 0 ? `~${hours}h${rem ? String(rem).padStart(2, "0") : ""}` : `~${mins} min`;
  const finals =
    finalsMode === "knockout"
      ? `${qualifierCount} in knockout`
      : `${qualifierCount} on final table`;
  return `${playerCount} players · ${poolCount} pools · ${poolMatches} pool matches · ${finals} · ${duration} on tables`;
}

function buildTournamentSchedule(input = {}) {
  const categoriesIn = Array.isArray(input.categories) ? input.categories : [];
  const playersIn = Array.isArray(input.players) ? input.players : [];
  const defaultPoolSize = clampInt(input.pool_size, 3, 8, 4);
  const defaultQualify = clampInt(input.qualify_count, 1, 4, 2);
  const tables = normalizeTables(input.tables, input.table_count);
  const matchMinutes = clampInt(input.match_minutes, 10, 60, 20);
  const warnings = [];

  const categoryPlans = [];
  const poolMatchGroups = [];
  const finalsMatches = [];
  let maxPoolRounds = 0;
  let totalPoolMatches = 0;
  let totalPools = 0;
  let entryCount = 0;

  categoriesIn.forEach((cat, catIndex) => {
    const categoryId = cat.id || `cat-${catIndex + 1}`;
    const poolSize = clampInt(cat.pool_size, 3, 8, defaultPoolSize);
    const qualifyCount = clampInt(cat.qualify_count, 1, 4, defaultQualify);
    const inCat = sortPlayers(
      playersIn.filter((p) =>
        (p.category_ids || []).map(String).includes(String(categoryId))
      )
    );
    entryCount += inCat.length;
    if (inCat.length < 2) {
      warnings.push(`${cat.name || "Category"} needs at least 2 players.`);
      categoryPlans.push({
        id: categoryId,
        name: cat.name || `Category ${catIndex + 1}`,
        pool_size: poolSize,
        qualify_count: qualifyCount,
        finals_mode: "final_table",
        pools: [],
        qualifier_count: 0,
      });
      return;
    }

    const sizes = poolSizesFor(inCat.length, poolSize);
    const pools = snakeSeed(inCat, sizes);
    totalPools += pools.length;
    if (pools.some((p) => p.players.length === 2)) {
      warnings.push(`${cat.name || "Category"} has a pool of 2.`);
    }

    const roundsByPool = pools.map((pool) =>
      roundRobinRounds(pool.players.map((p) => p.id))
    );
    maxPoolRounds = Math.max(maxPoolRounds, ...roundsByPool.map((r) => r.length), 0);

    const poolMatches = [];
    roundsByPool.forEach((rounds, poolIndex) => {
      const letter = pools[poolIndex].letter;
      rounds.forEach((pairs, roundIndex) => {
        pairs.forEach(([a, b]) => {
          poolMatches.push({
            category_id: categoryId,
            pool_letter: letter,
            stage: "pool",
            round_key: `pool-${roundIndex + 1}`,
            round_label: `Pool ${letter} R${roundIndex + 1}`,
            player_a_id: a,
            player_b_id: b,
            placeholder_a: null,
            placeholder_b: null,
            is_bye: false,
            round_index: roundIndex,
          });
        });
      });
    });
    totalPoolMatches += poolMatches.length;
    poolMatchGroups.push(poolMatches);

    const seeds = knockoutSeeds(pools, qualifyCount);
    const finalsMode = pickFinalsMode(seeds.length, cat.finals_mode || input.finals_mode);
    const finals =
      finalsMode === "knockout"
        ? buildKnockoutMatches(categoryId, seeds)
        : buildFinalTableMatches(categoryId, seeds);
    finalsMatches.push(...finals);

    categoryPlans.push({
      id: categoryId,
      name: cat.name || `Category ${catIndex + 1}`,
      pool_size: poolSize,
      qualify_count: qualifyCount,
      finals_mode: finalsMode,
      pools: pools.map((pool) => ({
        letter: pool.letter,
        player_ids: pool.players.map((p) => p.id),
        players: pool.players.map((p) => ({
          id: p.id,
          name: p.name,
          club_name: p.club_name || null,
          ranking_points: p.ranking_points == null ? null : Number(p.ranking_points),
        })),
      })),
      qualifier_count: seeds.length,
    });
  });

  const overlapping = [];
  playersIn.forEach((p) => {
    if ((p.category_ids || []).length > 1) overlapping.push(p.name || p.id);
  });
  if (overlapping.length) {
    warnings.push(
      `${overlapping.length} player(s) enter more than one category — they will not play two matches at once.`
    );
  }

  const orderedPoolMatches = [];
  for (let r = 0; r < maxPoolRounds; r += 1) {
    poolMatchGroups.forEach((group) => {
      group
        .filter((m) => m.round_index === r)
        .forEach((m) => orderedPoolMatches.push(m));
    });
  }

  const packed = packMatches([...orderedPoolMatches, ...finalsMatches], tables);
  const real = packed.filter((m) => !m.is_bye);
  const slotCount =
    real.reduce((max, m) => Math.max(max, (m.slot_index || 0) + 1), 0) || 0;
  const finalsModes = [
    ...new Set(categoryPlans.map((c) => c.finals_mode).filter(Boolean)),
  ];
  const qualifierCount = categoryPlans.reduce(
    (sum, c) => sum + (c.qualifier_count || 0),
    0
  );

  if (slotCount * matchMinutes > 300) {
    warnings.push("This draw may run longer than 5 hours on the chosen tables.");
  }

  return {
    categories: categoryPlans,
    matches: packed.map((m, i) => ({
      temp_id: `m${i + 1}`,
      category_id: m.category_id,
      pool_letter: m.pool_letter,
      stage: m.stage,
      round_key: m.round_key,
      round_label: m.round_label,
      slot_index: m.slot_index,
      table_id: m.table_id,
      table_number: m.table_number,
      player_a_id: m.player_a_id,
      player_b_id: m.player_b_id,
      placeholder_a: m.placeholder_a,
      placeholder_b: m.placeholder_b,
    })),
    tables,
    slot_count: slotCount,
    estimated_minutes: slotCount * matchMinutes,
    match_minutes: matchMinutes,
    headline: summarizeHeadline({
      playerCount: entryCount,
      poolCount: totalPools,
      poolMatches: totalPoolMatches,
      finalsMode: finalsModes.length === 1 ? finalsModes[0] : "mixed",
      qualifierCount,
      slotCount,
      matchMinutes,
    }),
    warnings,
    pool_match_count: totalPoolMatches,
    finals_match_count: packed.filter(
      (m) => m.stage !== "pool" && !m.is_bye
    ).length,
  };
}

function defaultTournamentName(date = new Date()) {
  const label = date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
  return `Club tournament · ${label}`;
}

const CATEGORY_PRESETS = ["Open", "Seniors", "Women", "U18", "U15", "U13"];

export {
  CATEGORY_PRESETS,
  poolSizesFor,
  snakeSeed,
  roundRobinRounds,
  pickFinalsMode,
  knockoutSeeds,
  buildKnockoutMatches,
  buildFinalTableMatches,
  packMatches,
  normalizeTables,
  buildTournamentSchedule,
  defaultTournamentName,
  sortPlayers,
};
